import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { saudeService } from '../../services/saude.service';
import PageContainer from '../../components/PageContainer';
import PageHeader from '../../components/PageHeader';
import { useToast } from '../../components/Toast';
import UploadAtestadoModal from '../../components/Saude/UploadAtestadoModal';
import DecisaoAbonoModal from '../../components/Saude/DecisaoAbonoModal';
import TimelineSaudeModal from '../../components/Saude/TimelineSaudeModal';
import CriteriosPericiaisModal from '../../components/Saude/CriteriosPericiaisModal';
import ConfigurarRepresentanteModal from '../../components/Saude/ConfigurarRepresentanteModal';
import { formatCPF } from '../../utils/formatters';
import {
  DocumentPlusIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  HeartIcon,
  SparklesIcon,
  ArrowPathIcon,
  ChartBarIcon,
  QueueListIcon,
  BuildingOffice2Icon,
  BriefcaseIcon,
  UserGroupIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import type { ColaboradorAtestadoContract } from '@sstexames/shared-types';

export default function GestaoAtestadosPage() {
  const { empresa } = useEmpresa();
  const toast = useToast();
  const [tabAtiva, setTabAtiva] = useState<'FILA' | 'INDICADORES'>('FILA');
  const [busca, setBusca] = useState('');
  const [filtroStatusAdmin, setFiltroStatusAdmin] = useState<string>('');
  const [filtroStatusAutenticidade, setFiltroStatusAutenticidade] = useState<string>('');

  // Modais
  const [modalUploadOpen, setModalUploadOpen] = useState(false);
  const [modalRepresentanteOpen, setModalRepresentanteOpen] = useState(false);
  const [atestadoSelecionadoParaAbono, setAtestadoSelecionadoParaAbono] =
    useState<ColaboradorAtestadoContract | null>(null);
  const [atestadoParaCriterios, setAtestadoParaCriterios] =
    useState<ColaboradorAtestadoContract | null>(null);
  const [colaboradorParaTimeline, setColaboradorParaTimeline] = useState<{
    id: number;
    nome: string;
  } | null>(null);
  const [reprocessandoId, setReprocessandoId] = useState<number | null>(null);

  const empresaId = empresa?.id || 0;

  // Busca atestados com polling reativo enquanto houver itens em processamento pela IA
  const {
    data: atestados = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'atestados-empresa',
      empresaId,
      filtroStatusAdmin,
      filtroStatusAutenticidade,
      busca,
    ],
    queryFn: () =>
      saudeService.listarAtestados({
        empresa_id: empresaId,
        status_administracao: filtroStatusAdmin || undefined,
        status_autenticidade: filtroStatusAutenticidade || undefined,
        busca: busca || undefined,
      }),
    enabled: !!empresaId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data)) {
        const hasProcessing = data.some(
          (a: ColaboradorAtestadoContract) =>
            a.status_processamento === 'RECEBIDO' ||
            a.status_processamento === 'PROCESSANDO' ||
            a.status_processamento === 'EXTRAINDO',
        );
        return hasProcessing ? 4000 : false;
      }
      return false;
    },
  });

  // Busca métricas ricas de Absenteísmo e Epidemiologia
  const { data: dashboard, refetch: refetchDashboard } = useQuery({
    queryKey: ['saude-dashboard-empresa', empresaId],
    queryFn: () => saudeService.obterDashboardSaudeEmpresa(empresaId),
    enabled: !!empresaId,
  });

  const handleReprocessar = async (atestadoId: number) => {
    try {
      setReprocessandoId(atestadoId);
      await saudeService.reprocessarAtestado(atestadoId);
      toast.success('Reanálise solicitada. A IA e a verificação pericial foram reenviadas para fila.');
      refetch();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(
        err?.response?.data?.message || 'Falha ao solicitar reprocessamento do atestado.',
      );
    } finally {
      setReprocessandoId(null);
    }
  };

  // Cores e Badges para Dimensão de Administração RH
  const renderBadgeAdmin = (status: string) => {
    switch (status) {
      case 'ABONO_ACEITO':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircleIcon className="w-3.5 h-3.5" />
            Abono Aprovado
          </span>
        );
      case 'ABONO_RECUSADO':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <XCircleIcon className="w-3.5 h-3.5" />
            Abono Recusado
          </span>
        );
      case 'EM_ANALISE':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <ClockIcon className="w-3.5 h-3.5" />
            Pendente RH
          </span>
        );
    }
  };

  // Cores e Badges para Dimensão de Autenticidade Pericial no Grid (Direto, Prático e Objetivo)
  const renderBadgeAutenticidade = (atestado: ColaboradorAtestadoContract) => {
    const status = atestado.status_autenticidade;
    const score = atestado.score_confiabilidade_ia != null ? Math.round(atestado.score_confiabilidade_ia * 100) : 98;

    if (status === 'CONFIRMADO') {
      return (
        <button
          type="button"
          onClick={() => setAtestadoParaCriterios(atestado)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition shadow-2xs"
          title="Ver critérios da auditoria"
        >
          <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-600" />
          <span>100% Confirmado Autêntico</span>
        </button>
      );
    }

    if (status === 'FALSIDADE_CONFIRMADA') {
      return (
        <button
          type="button"
          onClick={() => setAtestadoParaCriterios(atestado)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 transition shadow-2xs"
          title="Ver indícios de falsidade"
        >
          <XCircleIcon className="w-3.5 h-3.5 text-rose-600" />
          <span>100% Declarado Falso</span>
        </button>
      );
    }

    if (status === 'INCONCLUSIVO') {
      return (
        <button
          type="button"
          onClick={() => setAtestadoParaCriterios(atestado)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition shadow-2xs"
          title="Ver análise da triagem"
        >
          <span>50% Neutro / Em Análise</span>
        </button>
      );
    }

    // Probabilidade estimada da IA enquanto tramita na instituição
    if (score >= 70) {
      return (
        <button
          type="button"
          onClick={() => setAtestadoParaCriterios(atestado)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition shadow-2xs"
          title="Clique para ver os critérios avaliados pela IA"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span>{score}% Provável Autêntico</span>
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => setAtestadoParaCriterios(atestado)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition shadow-2xs"
        title="Clique para ver os critérios avaliados pela IA"
      >
        <span className="w-2 h-2 rounded-full bg-rose-500" />
        <span>{100 - score}% Provável Falso</span>
      </button>
    );
  };

  // Badge da tipologia documental
  const renderTipoDocumentoBadge = (tipo?: string, isAcompanhante?: boolean) => {
    if (isAcompanhante || tipo === 'ATESTADO_ACOMPANHANTE') {
      return (
        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
          Acompanhante
        </span>
      );
    }
    if (tipo === 'DECLARACAO_COMPARECIMENTO') {
      return (
        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
          Declaração de Comparecimento
        </span>
      );
    }
    if (tipo === 'ATESTADO_ODONTOLOGICO') {
      return (
        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-200">
          Odontológico
        </span>
      );
    }
    return (
      <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
        Atestado Médico
      </span>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Gestão de Absenteísmo & Homologação"
        description="Rastreamento pericial de atestados, confirmação automatizada com instituições de saúde, abono em folha e inteligência populacional contra faltas repetitivas."
        breadcrumbs={[
          { label: 'Gestão de Absenteísmo', href: '/saude/atestados' },
          { label: 'Homologação e Indicadores' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setModalRepresentanteOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold shadow-2xs transition"
              title="Configurar representante da empresa e máscara de assinatura do ofício de verificação"
            >
              <PencilSquareIcon className="w-4 h-4 text-gray-500" />
              <span>Assinatura do Ofício</span>
            </button>
            <button
              type="button"
              onClick={() => setModalUploadOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold shadow-xs transition"
            >
              <DocumentPlusIcon className="w-4 h-4" />
              Upload em Lote / Novo Atestado
            </button>
          </div>
        }
      />

      {/* Navegação por Tabs Estruturadas Clean */}
      <div className="mt-4 border-b border-gray-200">
        <nav className="flex space-x-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setTabAtiva('FILA')}
            className={`pb-3 px-1 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 transition ${
              tabAtiva === 'FILA'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <QueueListIcon className="w-4 h-4" />
            <span>Fila de Atestados & Homologação</span>
            <span
              className={`ml-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
                tabAtiva === 'FILA'
                  ? 'bg-gray-100 text-gray-900 font-bold'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {atestados.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setTabAtiva('INDICADORES');
              refetchDashboard();
            }}
            className={`pb-3 px-1 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 transition ${
              tabAtiva === 'INDICADORES'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChartBarIcon className="w-4 h-4" />
            <span>Inteligência Epidemiológica & Absenteísmo</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              Analítico
            </span>
          </button>
        </nav>
      </div>

      {/* Conteúdo da Tab 1: Fila de Atestados & Homologação */}
      {tabAtiva === 'FILA' && (
        <div className="space-y-6 mt-6 animate-fadeIn">
          {/* Cards de Métricas e Absenteísmo Executivo Clean */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Total de Atestados</span>
                <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.total_atestados_periodo ?? atestados.length}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Neste período de apuração</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Dias Afastados</span>
                <ClockIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.total_dias_afastamento ?? 0}{' '}
                <span className="text-sm font-normal text-gray-500">dias</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Impacto direto na produção</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Taxa de Absenteísmo</span>
                <HeartIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.taxa_absenteismo_percentual ?? 0}%
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Fórmula padrão OIT</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Pendentes de Abono</span>
                <SparklesIcon className="w-5 h-5 text-amber-500" />
              </div>
              <div className="mt-2 text-2xl font-bold text-amber-700">
                {dashboard?.atestados_em_analise_abono ?? 0}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Aguardando parecer do RH</p>
            </div>
          </div>

          {/* Barra de Filtros e Busca */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-2xs flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por colaborador, CPF, CID-10 ou médico..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                value={filtroStatusAdmin}
                onChange={(e) => setFiltroStatusAdmin(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2.5 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Todos os Status de Abono</option>
                <option value="EM_ANALISE">Pendente RH</option>
                <option value="ABONO_ACEITO">Abono Aprovado</option>
                <option value="ABONO_RECUSADO">Abono Recusado</option>
              </select>

              <select
                value={filtroStatusAutenticidade}
                onChange={(e) => setFiltroStatusAutenticidade(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2.5 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Todas as Autenticidades</option>
                <option value="CONFIRMADO">Autêntico Confirmado</option>
                <option value="AGUARDANDO_INSTITUICAO">Aguardando UBS/Clínica</option>
                <option value="NAO_VERIFICADO">Não Verificado</option>
                <option value="FALSIDADE_CONFIRMADA">Fraude Confirmada</option>
              </select>

              <button
                type="button"
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition"
                title="Atualizar Lista"
              >
                <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Tabela de Atestados */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                <thead className="bg-gray-50 text-gray-600 font-semibold text-left">
                  <tr>
                    <th className="py-3 px-4">Colaborador & Tipo</th>
                    <th className="py-3 px-4">Período / Afastamento</th>
                    <th className="py-3 px-4">CID-10 & Diagnóstico</th>
                    <th className="py-3 px-4">Médico / Estabelecimento</th>
                    <th className="py-3 px-4">Decisão RH (Abono)</th>
                    <th className="py-3 px-4">Autenticidade Pericial</th>
                    <th className="py-3 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        <ArrowPathIcon className="w-5 h-5 animate-spin mx-auto mb-2 text-gray-600" />
                        Carregando atestados...
                      </td>
                    </tr>
                  ) : atestados.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        Nenhum atestado encontrado com os filtros aplicados.
                      </td>
                    </tr>
                  ) : (
                    atestados.map((atestado: ColaboradorAtestadoContract) => (
                      <tr
                        key={atestado.id}
                        className={`hover:bg-gray-50/70 transition ${
                          atestado.is_duplicado ? 'bg-amber-50/20' : ''
                        }`}
                      >
                        {/* Colaborador & Tipo */}
                        <td className="py-3 px-4">
                          <div className="flex flex-col text-left">
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              {renderTipoDocumentoBadge(
                                atestado.tipo_documento,
                                atestado.is_acompanhante,
                              )}
                              {atestado.is_duplicado && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                                  <ExclamationTriangleIcon className="w-3 h-3 text-amber-600" />
                                  Duplicado (Ref #{atestado.duplicado_referencia_id || '1'})
                                </span>
                              )}
                            </div>

                            {atestado.colaborador_id ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setColaboradorParaTimeline({
                                    id: atestado.colaborador_id!,
                                    nome: atestado.colaborador_nome || `#${atestado.colaborador_id}`,
                                  })
                                }
                                className="font-bold text-gray-900 hover:text-gray-600 text-left transition"
                              >
                                {atestado.colaborador_nome || `#${atestado.colaborador_id}`}
                              </button>
                            ) : atestado.paciente_nome ? (
                              <div className="flex flex-col">
                                <span className="font-bold text-gray-900">
                                  {atestado.paciente_nome}
                                </span>
                                <span className="text-[10px] text-amber-700 font-medium bg-amber-50 px-1.5 py-0.5 rounded w-fit mt-0.5 border border-amber-200">
                                  Não vinculado ao quadro
                                </span>
                              </div>
                            ) : atestado.status_processamento === 'RECEBIDO' || atestado.status_processamento === 'PROCESSANDO' ? (
                              <span className="text-xs text-gray-500 italic font-medium flex items-center gap-1">
                                <SparklesIcon className="w-3 h-3 animate-spin text-gray-400" />
                                Lendo documento...
                              </span>
                            ) : (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs text-gray-500 italic">
                                  Paciente não identificado
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleReprocessar(atestado.id)}
                                  disabled={reprocessandoId === atestado.id}
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded transition disabled:opacity-50"
                                  title="Reprocessar IA para tentar identificar novamente"
                                >
                                  <ArrowPathIcon className={`w-3 h-3 ${reprocessandoId === atestado.id ? 'animate-spin' : ''}`} />
                                  Tentar novamente
                                </button>
                              </div>
                            )}

                            {atestado.colaborador_cpf && (
                              <span className="text-[11px] text-gray-400 font-normal">
                                CPF: {formatCPF(atestado.colaborador_cpf)}
                              </span>
                            )}

                            {atestado.redirecionado_grupo_economico && (
                              <div
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-800 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 mt-1 max-w-full truncate"
                                title={
                                  atestado.aviso_redirecionamento ||
                                  `Colaborador alocado na unidade "${atestado.empresa_destino_nome || atestado.empresa_nome}" do Grupo Econômico.`
                                }
                              >
                                <span>🏢</span>
                                <span className="truncate">
                                  Grupo: {atestado.empresa_destino_nome || atestado.empresa_nome}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Período / Dias */}
                        <td className="py-3 px-4">
                          {atestado.data_inicio ? (
                            <>
                              <div className="font-semibold text-gray-900">
                                {new Date(atestado.data_inicio).toLocaleDateString('pt-BR')}
                              </div>
                              <div className="text-[11px] text-gray-600 font-medium">
                                {atestado.dias_afastamento || 1} dia(s)
                                {atestado.horas_afastamento ? ` (${atestado.horas_afastamento}h)` : ''}
                              </div>
                            </>
                          ) : atestado.status_processamento === 'RECEBIDO' || atestado.status_processamento === 'PROCESSANDO' ? (
                            <span className="text-xs text-gray-500 italic font-medium flex items-center gap-1">
                              <SparklesIcon className="w-3 h-3 animate-spin text-gray-400" />
                              Extraindo via IA...
                            </span>
                          ) : (
                            <div>
                              <div className="font-semibold text-gray-900">
                                {atestado.data_emissao
                                  ? new Date(atestado.data_emissao).toLocaleDateString('pt-BR')
                                  : 'Data não informada'}
                              </div>
                              <div className="text-[11px] text-gray-600 font-medium">
                                {atestado.dias_afastamento || 1} dia(s)
                              </div>
                            </div>
                          )}
                        </td>

                        {/* CID & Diagnóstico Completo */}
                        <td className="py-3 px-4 max-w-xs">
                          {atestado.motivo_cid ? (
                            <div>
                              <span className="font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                {atestado.motivo_cid}
                              </span>
                              {atestado.motivo_cid_descricao ? (
                                <p
                                  className="text-[11px] text-gray-600 font-medium mt-1 leading-snug"
                                  title={atestado.motivo_cid_descricao}
                                >
                                  {atestado.motivo_cid_descricao}
                                </p>
                              ) : (
                                <p className="text-[10px] text-gray-400 italic mt-0.5">
                                  Diagnóstico em validação
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              CID Omitido (Sigilo CFM)
                            </span>
                          )}
                        </td>

                        {/* Médico */}
                        <td className="py-3 px-4">
                          <div className="text-gray-900 font-medium">
                            {atestado.medico_nome || 'Médico não identificado'}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {atestado.medico_crm
                              ? `CRM ${atestado.medico_crm}/${atestado.medico_uf || ''}`
                              : ''}
                            {atestado.instituicao_nome ? ` • ${atestado.instituicao_nome}` : ''}
                          </div>
                        </td>

                        {/* Decisão RH */}
                        <td className="py-3 px-4">
                          {renderBadgeAdmin(atestado.status_administracao)}
                        </td>

                        {/* Autenticidade Pericial */}
                        <td className="py-3 px-4">
                          {renderBadgeAutenticidade(atestado)}
                        </td>

                        {/* Ações */}
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleReprocessar(atestado.id)}
                              disabled={
                                reprocessandoId === atestado.id ||
                                atestado.status_processamento === 'PROCESSANDO'
                              }
                              className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition disabled:opacity-50"
                              title="Reprocessar Documento via IA (Retry)"
                            >
                              <ArrowPathIcon
                                className={`w-4 h-4 ${
                                  reprocessandoId === atestado.id ||
                                  atestado.status_processamento === 'PROCESSANDO'
                                    ? 'animate-spin text-indigo-600'
                                    : ''
                                }`}
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => setAtestadoSelecionadoParaAbono(atestado)}
                              className="px-2.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs transition"
                              title="Decidir Abono para a Folha de Pagamento"
                            >
                              Decisão RH
                            </button>
                            {atestado.detalhes_verificacao?.oficio_pdf_url && (
                              <a
                                href={atestado.detalhes_verificacao.oficio_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition"
                                title="Baixar cópia do Ofício Formal enviado à instituição em PDF"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() =>
                                setColaboradorParaTimeline({
                                  id: atestado.colaborador_id,
                                  nome: atestado.colaborador_nome || `#${atestado.colaborador_id}`,
                                })
                              }
                              className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                              title="Ver Prontuário Longitudinal de Saúde"
                            >
                              <HeartIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo da Tab 2: Inteligência Epidemiológica & Absenteísmo Clean */}
      {tabAtiva === 'INDICADORES' && (
        <div className="space-y-6 mt-6 animate-fadeIn">
          
          {/* Header Clean - 4 Cards de Métricas e Custo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Taxa Geral de Absenteísmo</span>
                <HeartIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.taxa_absenteismo_percentual ?? 0}%
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Padrão OIT (dias perdidos / úteis)</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Dias Afastados Totais</span>
                <ClockIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.total_dias_afastamento ?? 0}{' '}
                <span className="text-sm font-normal text-gray-500">dias</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Impacto direto acumulado</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Custo Estimado da Parada</span>
                <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                R$ {(dashboard?.custo_estimado_afastamento || 0).toLocaleString('pt-BR')}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Diárias diretas + encargos</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
              <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <span>Vidas Monitoradas</span>
                <UserGroupIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {dashboard?.total_colaboradores_ativos ?? 0}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Colaboradores ativos na empresa</p>
            </div>
          </div>

          {/* Grid de Gráficos e Distribuições Analíticas Clean */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 1. Absenteísmo por Setor */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-4">
                <BuildingOffice2Icon className="w-5 h-5 text-gray-600" />
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Afastamentos por Setor</h3>
                  <p className="text-[11px] text-gray-500">Volume de dias e ocorrências por departamento</p>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {(!dashboard?.distribuicao_por_setor || dashboard.distribuicao_por_setor.length === 0) ? (
                  <p className="text-xs text-gray-400 italic py-6 text-center">Nenhum afastamento registrado por setor neste período.</p>
                ) : (
                  dashboard.distribuicao_por_setor.map((setor, idx) => {
                    const totalGeralDias = dashboard.total_dias_afastamento || 1;
                    const percentual = Math.min(100, Math.round((setor.dias_afastamento / totalGeralDias) * 100));

                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-xs font-semibold">
                          <span className="text-gray-800">{setor.setor_nome}</span>
                          <span className="text-gray-700 font-bold">
                            {setor.dias_afastamento} dia(s) • {setor.total_atestados} evento(s)
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gray-700 h-2 rounded-full transition-all duration-700 ease-out"
                            style={{ width: `${percentual}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 2. Absenteísmo por Cargo */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-4">
                <BriefcaseIcon className="w-5 h-5 text-gray-600" />
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Afastamentos por Cargo</h3>
                  <p className="text-[11px] text-gray-500">Funções com maior frequência de atestados</p>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {(!dashboard?.distribuicao_por_cargo || dashboard.distribuicao_por_cargo.length === 0) ? (
                  <p className="text-xs text-gray-400 italic py-6 text-center">Nenhum dado por função catalogado no período.</p>
                ) : (
                  dashboard.distribuicao_por_cargo.map((cargo, idx) => {
                    const totalGeralDias = dashboard.total_dias_afastamento || 1;
                    const percentual = Math.min(100, Math.round((cargo.dias_afastamento / totalGeralDias) * 100));

                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-xs font-semibold">
                          <span className="text-gray-800">{cargo.cargo_nome}</span>
                          <span className="text-gray-700 font-bold">
                            {cargo.dias_afastamento} dia(s)
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gray-500 h-2 rounded-full transition-all duration-700 ease-out"
                            style={{ width: `${percentual}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Grid: Ranking de CIDs & Fator Bradford */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 3. Ranking de CIDs & Patologias */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-4">
                <HeartIcon className="w-5 h-5 text-gray-600" />
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Ranking Epidemiológico de CIDs</h3>
                  <p className="text-[11px] text-gray-500">Patologias mais prevalentes na empresa</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-gray-500 font-semibold border-b border-gray-100">
                    <tr>
                      <th className="py-2 text-left">CID / Patologia</th>
                      <th className="py-2 text-center">Casos</th>
                      <th className="py-2 text-center">Dias</th>
                      <th className="py-2 text-right">% Impacto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(!dashboard?.ranking_cids_detalhado || dashboard.ranking_cids_detalhado.length === 0) ? (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-gray-400">
                          Nenhum diagnóstico registrado.
                        </td>
                      </tr>
                    ) : (
                      dashboard.ranking_cids_detalhado.map((cidItem, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="py-2.5 pr-2">
                            <span className="font-bold text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 mr-1.5">
                              {cidItem.cid}
                            </span>
                            <span className="text-gray-800 font-medium">
                              {cidItem.descricao || 'Patologia não especificada'}
                            </span>
                          </td>
                          <td className="py-2.5 text-center font-bold text-gray-800">
                            {cidItem.total_ocorrencias}
                          </td>
                          <td className="py-2.5 text-center font-bold text-gray-700">
                            {cidItem.total_dias_perdidos}
                          </td>
                          <td className="py-2.5 text-right font-bold text-gray-900">
                            {cidItem.percentual}%
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4. Fator Bradford (Bradford Factor) */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheckIcon className="w-5 h-5 text-gray-600" />
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Fator Bradford (Anti-Recorrência)</h3>
                  <p className="text-[11px] text-gray-500">Métrica global que identifica faltas curtas repetitivas (S² × D)</p>
                </div>
              </div>

              <p className="text-[11px] text-gray-500 mb-4 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                💡 <strong>Como funciona o Fator Bradford?</strong> Ausências curtas frequentes (ex.: 5 faltas de 1 dia = 125 pts) desestruturam a operação muito mais que um único afastamento longo (1 falta de 5 dias = 5 pts). Scores acima de 125 exigem atenção do RH e medicina ocupacional.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-gray-500 font-semibold border-b border-gray-100">
                    <tr>
                      <th className="py-2 text-left">Colaborador</th>
                      <th className="py-2 text-center">Faltas (S)</th>
                      <th className="py-2 text-center">Dias (D)</th>
                      <th className="py-2 text-center">Bradford</th>
                      <th className="py-2 text-right">Risco</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(!dashboard?.fator_bradford || dashboard.fator_bradford.length === 0) ? (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-gray-400">
                          Nenhum colaborador com afastamentos registrados.
                        </td>
                      </tr>
                    ) : (
                      dashboard.fator_bradford.map((bItem, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="py-2.5 font-bold text-gray-800">
                            {bItem.colaborador_nome}
                          </td>
                          <td className="py-2.5 text-center font-semibold text-gray-700">
                            {bItem.total_ausencias}
                          </td>
                          <td className="py-2.5 text-center font-semibold text-gray-700">
                            {bItem.total_dias}
                          </td>
                          <td className="py-2.5 text-center font-extrabold text-gray-900">
                            {bItem.score_bradford}
                          </td>
                          <td className="py-2.5 text-right">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                bItem.nivel_risco === 'CRITICO'
                                  ? 'bg-rose-50 text-rose-800 border border-rose-200'
                                  : bItem.nivel_risco === 'ALTO'
                                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                  : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              }`}
                            >
                              {bItem.nivel_risco}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Grid: Demografia & Geografia Clean */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Demografia: Faixa Etária */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <UserGroupIcon className="w-5 h-5 text-gray-600" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Por Faixa Etária
                </h4>
              </div>
              <div className="space-y-2 mt-2">
                {dashboard?.demografia?.por_faixa_etaria.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                    <span className="text-gray-600">{f.faixa}</span>
                    <span className="font-bold text-gray-900">{f.dias} dias ({f.total} casos)</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Demografia: Tempo de Casa */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="w-5 h-5 text-gray-600" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Por Tempo de Casa
                </h4>
              </div>
              <div className="space-y-2 mt-2">
                {dashboard?.demografia?.por_tempo_casa.map((t, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                    <span className="text-gray-600">{t.faixa}</span>
                    <span className="font-bold text-gray-900">{t.dias} dias</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Geografia / UFs */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <MapPinIcon className="w-5 h-5 text-gray-600" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Origem dos Atestados (UF)
                </h4>
              </div>
              <div className="space-y-2 mt-2">
                {dashboard?.distribuicao_geografica?.map((geo, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                    <span className="font-semibold text-gray-800">Estado: {geo.uf}</span>
                    <span className="font-bold text-gray-900">{geo.count} atestado(s)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload */}
      <UploadAtestadoModal
        isOpen={modalUploadOpen}
        onClose={() => setModalUploadOpen(false)}
        empresaId={empresaId}
        onSuccess={() => {
          refetch();
          refetchDashboard();
        }}
        toast={toast}
      />

      {/* Modal de Decisão de Abono */}
      <DecisaoAbonoModal
        isOpen={!!atestadoSelecionadoParaAbono}
        onClose={() => setAtestadoSelecionadoParaAbono(null)}
        atestado={atestadoSelecionadoParaAbono}
        onSuccess={() => {
          refetch();
          refetchDashboard();
        }}
        toast={toast}
      />

      {/* Modal de Auditoria e Critérios Periciais Clean */}
      <CriteriosPericiaisModal
        isOpen={!!atestadoParaCriterios}
        onClose={() => setAtestadoParaCriterios(null)}
        atestado={atestadoParaCriterios}
      />

      {/* Modal de Linha do Tempo / Prontuário Longitudinal */}
      {colaboradorParaTimeline && (
        <TimelineSaudeModal
          isOpen={!!colaboradorParaTimeline}
          onClose={() => setColaboradorParaTimeline(null)}
          colaboradorId={colaboradorParaTimeline.id}
          colaboradorNome={colaboradorParaTimeline.nome}
        />
      )}

      {/* Modal de Configuração de Representante e Assinatura do Ofício */}
      <ConfigurarRepresentanteModal
        isOpen={modalRepresentanteOpen}
        onClose={() => setModalRepresentanteOpen(false)}
        empresaId={empresaId}
        empresaNome={empresa?.nome}
        toast={toast}
      />
    </PageContainer>
  );
}
